import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faLock, faTimes, faCreditCard } from '@/icons';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthContext } from '../contexts/AuthContext';
import { useTeamBillingState, TEAM_BILLING_PATH } from '../hooks/useTeamBillingState';
import type { TeamBillingState } from '../types/auth';
import { formatMoney, formatLongDate } from '../utils/billingFormat';

export type PastDueBannerTone = 'warning' | 'error';

export interface PastDueBannerContent {
    tone: PastDueBannerTone;
    message: string;
    /** Label of the pay button, or null when the user cannot pay and must ask an administrator. */
    actionLabel: string | null;
    /** Shown instead of the pay button. */
    contactText: string | null;
    dismissible: boolean;
}

/**
 * What the banner says for a billing state. Null when nothing is owed. Pure, for tests.
 */
export function pastDueBannerContent(
    state: TeamBillingState | null | undefined,
    canManageBilling: boolean,
): PastDueBannerContent | null {
    if (!state || (state.status !== 'pastDue' && state.status !== 'readOnly')) return null;
    const requiresAction = state.paymentStatus === 'requires_action';
    const contactText = canManageBilling ? null : 'Contact your firm administrator.';

    if (state.status === 'readOnly') {
        return {
            tone: 'error',
            message: 'Your account is read-only until your subscription is paid.',
            actionLabel: canManageBilling ? (requiresAction ? 'Complete payment' : 'Pay now') : null,
            contactText,
            dismissible: false,
        };
    }

    let message: string;
    if (requiresAction) {
        message = 'Your bank needs you to confirm your renewal payment.';
    } else {
        const amount = formatMoney(state.amountDue);
        message = amount
            ? `Your subscription renewal of ${amount} is unpaid.`
            : 'Your subscription renewal is unpaid.';
    }
    const graceEnds = formatLongDate(state.graceEndsAt);
    if (graceEnds) message += ` Pay by ${graceEnds} to keep full access.`;

    return {
        tone: 'warning',
        message,
        actionLabel: canManageBilling ? (requiresAction ? 'Complete payment' : 'Pay now') : null,
        contactText,
        dismissible: true,
    };
}

interface PastDueBannerViewProps {
    content: PastDueBannerContent;
    onAction: () => void;
    onDismiss: () => void;
}

/** Presentational part of the banner. */
export const PastDueBannerView: React.FC<PastDueBannerViewProps> = ({ content, onAction, onDismiss }) => {
    const theme = useTheme();
    const isError = content.tone === 'error';
    const background = isError ? '#fef2f2' : '#fffbeb';
    const border = isError ? '#fecaca' : '#fde68a';
    const color = isError ? '#991b1b' : '#92400e';
    const buttonColor = isError ? '#b91c1c' : '#b45309';

    return (
        <div
            role={isError ? 'alert' : 'status'}
            data-testid="past-due-banner"
            data-tone={content.tone}
            className="rw-wrap"
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.sm,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: background,
                borderBottom: `1px solid ${border}`,
                color,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
            }}
        >
            <FontAwesomeIcon icon={isError ? faLock : faExclamationTriangle} style={{ fontSize: theme.typography.fontSize.sm }} />
            <span className="rw-wrap-text">{content.message}</span>
            {content.actionLabel ? (
                <button
                    type="button"
                    onClick={onAction}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: theme.spacing.xs,
                        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                        backgroundColor: buttonColor,
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: theme.borderRadius.sm,
                        cursor: 'pointer',
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.medium,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: theme.typography.fontSize.xs }} />
                    {content.actionLabel}
                </button>
            ) : content.contactText ? (
                <span style={{ fontWeight: theme.typography.fontWeight.normal }}>{content.contactText}</span>
            ) : null}
            {content.dismissible && (
                <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={onDismiss}
                    style={{
                        background: 'none',
                        border: 'none',
                        color,
                        cursor: 'pointer',
                        padding: theme.spacing.xs,
                        fontSize: theme.typography.fontSize.sm,
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            )}
        </div>
    );
};

/**
 * BillingDunning: tells a signed-in team that its renewal is unpaid (past due, amber) or that the
 * grace period ended (read-only, red). The past-due banner can be dismissed for this page load.
 */
export const PastDueBanner: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthContext();
    const { billingState, canManageBilling } = useTeamBillingState();
    const [dismissed, setDismissed] = useState(false);

    if (!isAuthenticated) return null;
    const content = pastDueBannerContent(billingState, canManageBilling);
    if (!content) return null;
    if (content.dismissible && dismissed) return null;

    return (
        <PastDueBannerView
            content={content}
            onAction={() => navigate(TEAM_BILLING_PATH)}
            onDismiss={() => setDismissed(true)}
        />
    );
};
